import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Calendar,
  Heart
} from 'lucide-react'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const ContactSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6"
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">İletişim & Randevu</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Randevunuzu
            <span className="text-gradient block">Hemen Alın</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Can dostlarınızın sağlığı için hızlı ve kolay randevu alın. 
            Acil durumlar için 7/24 ulaşabilirsiniz.
          </p>
        </motion.div>

        {/* Contact Information - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {/* Phone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex items-start space-x-4 p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl border border-primary-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="p-3 bg-primary-500 rounded-xl">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">Telefon</h3>
              <a
                href={toTelHref(contactInfo.phone)}
                className="text-primary-600 hover:text-primary-700 font-semibold text-lg transition-colors duration-200"
              >
                {contactInfo.phone}
              </a>
              <p className="text-gray-600 text-sm mt-1">Randevu ve bilgi için</p>
            </div>
          </motion.div>

          {/* WhatsApp */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex items-start space-x-4 p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="p-3 bg-green-500 rounded-xl">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
              <a
                href={toWhatsAppHref(contactInfo.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-semibold text-lg transition-colors duration-200"
              >
                {contactInfo.whatsapp}
              </a>
              <p className="text-gray-600 text-sm mt-1">Hızlı iletişim için</p>
            </div>
          </motion.div>

          {/* Email */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-start space-x-4 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="p-3 bg-blue-500 rounded-xl">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">E-posta</h3>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors duration-200"
              >
                {contactInfo.email}
              </a>
              <p className="text-gray-600 text-sm mt-1">Detaylı bilgi için</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Address & Working Hours - Full Width */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex items-start space-x-4 p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="p-3 bg-purple-500 rounded-xl">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">Adres</h3>
              <p className="text-gray-700 leading-relaxed">
                {contactInfo.address}
              </p>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 mt-3 text-purple-600 hover:text-purple-700 font-medium transition-colors duration-200"
              >
                <MapPin className="w-4 h-4" />
                <span>Haritada Gör</span>
              </motion.a>
            </div>
          </motion.div>

          {/* Working Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex items-start space-x-4 p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="p-3 bg-orange-500 rounded-xl">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">Çalışma Saatleri</h3>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Hafta içi:</span>
                  <span>{contactInfo.workingHours.weekdays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Hafta sonu:</span>
                  <span>{contactInfo.workingHours.weekend}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-semibold text-red-600">
                    {contactInfo.workingHours.emergency}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Emergency Contact Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 text-white text-center"
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Heart className="w-6 h-6 animate-bounce-gentle" />
            <h3 className="text-2xl font-bold">Acil Durum?</h3>
            <Heart className="w-6 h-6 animate-bounce-gentle" />
          </div>
          <p className="text-red-100 mb-6 text-lg">
            Can dostunuzun acil tıbbi müdahaleye ihtiyacı varsa hemen arayın!
          </p>
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={toTelHref(contactInfo.phone)}
                    className="inline-flex items-center space-x-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-50 transition-colors duration-200"
                  >
                    <Phone className="w-6 h-6" />
                    <span>ACİL ARAMA: {contactInfo.phone}</span>
                  </motion.a>
        </motion.div>
      </div>
    </section>
  )
}

export default ContactSection
